// #region Imports
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ClearIcon from "@mui/icons-material/Clear";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Popover from "@mui/material/Popover";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { skipToken } from "@reduxjs/toolkit/query";
import EntityMember from "_shared/entity-members/entity-member";
import { useFocusWhenAsked } from "_shared/fee-schedules/categories/_views/fee-schedule-category-editor-focus";
import FeeScheduleTeamRoster from "_shared/fee-schedules/categories/fee-schedule-team-roster";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import { type KeyboardEvent, useRef, useState } from "react";
import { useSession } from "users/__session/session-context";
import {
  proposalFeeScheduleRestrictedTeamMemberText,
  proposalFeeScheduleTeamMemberFallbackText,
  proposalFeeScheduleUnassignedText,
} from "work/proposals/negotiation/proposal-fee-schedule-aspects";
// #endregion

// #region Styled Components
const RosterPaper = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  maxHeight: 360,
  minHeight: 0,
}));
const RosterSearch = styled("div")(({ theme }) => ({
  flexShrink: 0,
  padding: theme.spacing(1.5, 1.5, 1),
}));
// The only part that scrolls: the search box above and the browse handle below stay where they are.
const RosterList = styled(List)(() => ({
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
})) as typeof List;
const RosterEntryLabels = styled("span")(({ theme }) => ({
  display: "inline-flex",
  flexShrink: 0,
  gap: theme.spacing(0.5),
  marginLeft: theme.spacing(1),
}));
const RosterEmpty = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  padding: theme.spacing(1, 2),
})) as typeof Typography;
const RosterFooter = styled("div")(({ theme }) => ({
  flexShrink: 0,
  padding: theme.spacing(1),
}));
// #endregion

// #region Types
type FeeScheduleTeamMemberPickerFieldProps = {
  /** Who the rate is for, or null for nobody. */
  teamMember: EntityMember | null;
  /** The server withholds who the rate is for from this viewer, and it is not theirs to change. */
  isMasked?: boolean;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  /**
   * The proposal's vendor team, offered first (#5798). Without it — a fee schedule template, which
   * has no team — the field opens the browse pane at once.
   */
  roster?: FeeScheduleTeamRoster;
  /**
   * Who the rate named when its editor opened: the one person off the team the field flags rather
   * than calls a newcomer. Defaults to whoever the field was first drawn with.
   */
  openedTeamMember?: EntityMember | null;
  /** Opens the browse pane, resolving to whoever was chosen, or to undefined when it was dismissed. */
  onBrowse: () => Promise<EntityMember | undefined>;
  onChange: (teamMember: EntityMember | null) => void;
  /**
   * Each new number moves the focus to the field, once it is drawn (#6097). The category editor passes
   * one when a page opened it from an issue about who bills the rate.
   */
  focusRequest?: number;
};
// #endregion

export const feeScheduleTeamMemberOffTeamText =
  "Not on the vendor team. Choose somebody on the team, or browse to add this person to it.";
export const feeScheduleTeamMemberMaskedTeamText =
  "The vendor team is hidden from you, so you cannot choose who this rate is for.";
export const feeScheduleTeamMemberJoinsTeamText = "Joins the vendor team when you save this category.";
export const feeScheduleTeamMemberWorksForClientText =
  "Works for the client. A rate is billed by the vendor, so choose somebody on the vendor team.";

/**
 * Who a fee schedule rate is for: their name, and a way to choose somebody else or nobody.
 *
 * On a proposal the field opens a short, searchable list of the vendor team — its leader, its
 * members, and on an active proposal the members one side proposed and the other has not accepted
 * yet, marked "Proposed" (#5798). A fixed "Browse for new member" handle below the list opens the
 * full picker, with its Coworkers, Network and Marketplace tabs, for somebody the team does not
 * have. The page adds that person to the team when the rate is saved. A rate may bill only somebody
 * on the vendor team — `WorkAgreement.ValidateFeeSchedule` refuses to hire otherwise — so the team
 * comes first and anybody else takes one more step.
 *
 * This reverses #5393, which removed a short list for duplicating the picker's Coworkers tab. That
 * list was the author's coworkers; this one is the team the rate will be billed under, which no tab
 * in the picker shows.
 *
 * A template has no team, so it has no list: its field opens the full picker straight away and is
 * otherwise the same field.
 *
 * Nobody from the client's own firm is offered, in the list or behind "Browse" (#6091): a rate is
 * what the client pays the vendor for. A rate that already names one is flagged.
 *
 * A rate that already named somebody off the team when the field opened is flagged, not changed.
 * Somebody just found through "Browse" is off the team too, but only until the rate is saved, so the
 * field says that instead. A team masked to this viewer offers nobody, and the field cannot be
 * changed.
 *
 * Read-only rather than disabled when it can be changed, so it keeps its label, its required marker
 * and its helper text in the colours of a live field. The name is looked up for the person, whoever
 * they are and however they were chosen — the store answers without a request for anybody already
 * read.
 */
export default function FeeScheduleTeamMemberPickerField(props: Readonly<FeeScheduleTeamMemberPickerFieldProps>) {
  const { teamMember, roster, onBrowse, onChange, focusRequest } = props;
  const isMasked = props.isMasked ?? false;
  const disabled = props.disabled ?? false;
  const required = props.required ?? false;
  const error = props.error ?? false;
  const session = useSession();
  const selfUserId = session.user?.id;

  const fieldRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [rosterAnchor, setRosterAnchor] = useState<HTMLElement | null>(null);
  const [query, setQuery] = useState("");
  // Who the rate named when the field opened, where the editor does not say. The editors say,
  // because they also remount the field when the fee stops or starts carrying a person.
  const [firstTeamMember] = useState(teamMember);
  const openedTeamMember = props.openedTeamMember === undefined ? firstTeamMember : props.openedTeamMember;

  const individualsQuery = useGetIndividualsByUserIdsQuery(
    teamMember && !isMasked ? { userIds: [teamMember.userId.value] } : skipToken,
  );
  const individual = teamMember
    ? (Individual.fromJSONArray(individualsQuery.data, { allowEmpty: true }) ?? []).find((candidate) =>
        candidate.userId.isEqualTo(teamMember.userId),
      )
    : undefined;

  const rosterEntries = roster?.offeredEntries ?? [];
  const rosterIndividualsQuery = useGetIndividualsByUserIdsQuery(
    rosterEntries.length > 0 ? { userIds: rosterEntries.map((entry) => entry.member.userId.value) } : skipToken,
  );
  const rosterNames = new Map(
    (Individual.fromJSONArray(rosterIndividualsQuery.data, { allowEmpty: true }) ?? []).map((candidate) => [
      candidate.userId.value,
      candidate.fullName,
    ]),
  );

  function getDisplayName(): string {
    if (isMasked) return proposalFeeScheduleRestrictedTeamMemberText;
    if (!teamMember) return proposalFeeScheduleUnassignedText;
    if (individual) return individual.fullName;
    // Blank while the name is on its way, rather than a word that reads as somebody's name.
    if (individualsQuery.isFetching || individualsQuery.isUninitialized) return "";
    return proposalFeeScheduleTeamMemberFallbackText;
  }

  function getHelperText(): { text?: string; isWarning: boolean } {
    if (props.helperText) return { text: props.helperText, isWarning: false };
    if (!roster || isMasked) return { isWarning: false };
    if (roster.isMasked) return { text: feeScheduleTeamMemberMaskedTeamText, isWarning: false };
    if (roster.isExcluded(teamMember)) return { text: feeScheduleTeamMemberWorksForClientText, isWarning: true };
    if (!roster.isOffTeam(teamMember)) return { isWarning: false };
    // The same rule the page applies on save: see `FeeScheduleTeamRoster.newcomerNamedBy`.
    if (roster.newcomerNamedBy(teamMember, openedTeamMember)) {
      return { text: feeScheduleTeamMemberJoinsTeamText, isWarning: false };
    }
    return { text: feeScheduleTeamMemberOffTeamText, isWarning: true };
  }

  useFocusWhenAsked(fieldRef, focusRequest);

  async function browse() {
    const chosen = await onBrowse();
    if (chosen) onChange(chosen);
  }

  function closeRoster() {
    setRosterAnchor(null);
    setQuery("");
  }

  function open() {
    if (!roster) {
      void browse();
      return;
    }
    setRosterAnchor(fieldRef.current);
  }

  function handleRosterEntryChosen(member: EntityMember) {
    closeRoster();
    onChange(member);
  }

  function handleBrowseChosen() {
    closeRoster();
    void browse();
  }

  function rosterEntryName(member: EntityMember): string {
    const name = rosterNames.get(member.userId.value);
    if (name) return name;
    // Blank while the names are on their way, as the field itself is.
    if (rosterIndividualsQuery.isFetching || rosterIndividualsQuery.isUninitialized) return "";
    return proposalFeeScheduleTeamMemberFallbackText;
  }

  const canChange = !disabled && !isMasked && !(roster?.isMasked ?? false);
  const displayName = getDisplayName();
  const helper = getHelperText();
  const normalizedQuery = query.trim().toLowerCase();
  const matchingEntries = rosterEntries.filter(
    (entry) => !normalizedQuery || rosterEntryName(entry.member).toLowerCase().includes(normalizedQuery),
  );

  function renderRosterContent() {
    if (rosterEntries.length === 0) return <RosterEmpty variant="body2">Nobody is on the vendor team yet.</RosterEmpty>;
    if (matchingEntries.length === 0) {
      return <RosterEmpty variant="body2">Nobody on the team matches &ldquo;{query.trim()}&rdquo;.</RosterEmpty>;
    }
    return matchingEntries.map((entry) => {
      const name = rosterEntryName(entry.member);
      const isSelf = Boolean(selfUserId && entry.member.userId.isEqualTo(selfUserId));
      return (
        <ListItemButton
          key={entry.member.toString()}
          selected={entry.member.isEqualTo(teamMember)}
          onClick={() => handleRosterEntryChosen(entry.member)}
        >
          <ListItemText primary={name} secondary={entry.isLeader ? "Team leader" : undefined} />
          {(isSelf || entry.isProposed) && (
            <RosterEntryLabels>
              {isSelf && <Chip label="You" size="small" color="primary" />}
              {entry.isProposed && (
                <Tooltip title="Proposed for the team. The other side has not accepted this yet.">
                  <Chip label="Proposed" size="small" color="warning" variant="outlined" />
                </Tooltip>
              )}
            </RosterEntryLabels>
          )}
        </ListItemButton>
      );
    });
  }

  return (
    <>
      <TextField
        ref={fieldRef}
        label={isMasked ? "Team Member (restricted)" : "Team Member"}
        value={displayName}
        disabled={!canChange}
        required={required}
        error={error}
        helperText={helper.text}
        size="small"
        fullWidth
        sx={{ marginTop: 2 }}
        onClick={canChange ? open : undefined}
        slotProps={{
          htmlInput: {
            readOnly: true,
            style: canChange ? { cursor: "pointer" } : undefined,
            // A read-only field that opens something answers the keys a select answers.
            onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => {
              if (!canChange || !["Enter", "ArrowDown", " "].includes(event.key)) return;
              event.preventDefault();
              open();
            },
          },
          formHelperText: helper.isWarning ? { sx: { color: "warning.dark" } } : undefined,
          input: {
            endAdornment: canChange && (
              <InputAdornment position="end">
                {/*
                  Clear sits first and the opener never moves, so the button that was just pressed is
                  still the one under the pointer.
                */}
                {teamMember && (
                  <Tooltip title="Name nobody">
                    <IconButton
                      aria-label="Clear team member"
                      onClick={(event) => {
                        event.stopPropagation();
                        onChange(null);
                      }}
                      size="small"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {roster ? (
                  <Tooltip title="Choose from the vendor team">
                    <IconButton aria-label="Choose a team member" edge="end" size="small">
                      <ArrowDropDownIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip title={teamMember ? "Choose someone else" : "Choose someone"}>
                    <IconButton aria-label="Find a team member" edge="end" size="small">
                      <PersonSearchOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </InputAdornment>
            ),
          },
        }}
      />
      {roster && (
        <Popover
          open={rosterAnchor !== null}
          anchorEl={rosterAnchor}
          onClose={closeRoster}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{
            // Straight into the search box, once the list is on screen: typing a name is the quick way
            // through a team of any size.
            transition: { onEntered: () => searchRef.current?.focus() },
            paper: {
              "aria-label": "Vendor team",
              role: "dialog",
              sx: { width: rosterAnchor?.offsetWidth, minWidth: 280 },
            },
          }}
        >
          <RosterPaper>
            <RosterSearch>
              <TextField
                inputRef={searchRef}
                fullWidth
                size="small"
                placeholder="Search the team"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                slotProps={{ htmlInput: { "aria-label": "Search the team" } }}
              />
            </RosterSearch>
            <RosterList dense aria-label="Vendor team members">
              {renderRosterContent()}
            </RosterList>
            <Divider />
            <RosterFooter>
              <Button fullWidth startIcon={<PersonAddAltOutlinedIcon />} onClick={handleBrowseChosen}>
                Browse for new member
              </Button>
            </RosterFooter>
          </RosterPaper>
        </Popover>
      )}
    </>
  );
}
